import React, { useState } from 'react';
import { Bell, Plus, Trash2 } from 'lucide-react';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { Button } from '../../../shared/components/Button';
import { SkeletonText } from '../../../shared/components/Skeleton';
import { TextInput } from '../../../shared/components/TextInput';
import {
  useConfigAlertasContrato,
  useCriarConfigAlerta,
  useDesativarConfigAlerta,
} from '../hooks';

interface Props {
  contractId: string;
  canEdit: boolean;
}

const ALERT_TYPE_LABEL: Record<string, string> = {
  vencimento: 'Vencimento',
  reajuste: 'Reajuste',
  garantia: 'Garantia',
};
const ALERT_TYPE_OPTIONS = Object.entries(ALERT_TYPE_LABEL).map(([value, label]) => ({ value, label }));

/**
 * Antecedências de alerta específicas deste contrato (RN-COM-010) —
 * sobrepõem o default global quando presentes
 * (`ContractAlertConfigService.getEffectiveAlerts`).
 */
export function ContractAlertConfigSection({ contractId, canEdit }: Props) {
  const { data: configs = [], isLoading } = useConfigAlertasContrato(contractId);
  const { mutate: criar, isPending: isCreating } = useCriarConfigAlerta();
  const { mutate: desativar } = useDesativarConfigAlerta();
  const [daysBefore, setDaysBefore] = useState('30');
  const [alertType, setAlertType] = useState('vencimento');

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
          <Bell className="w-4 h-4 text-gray-400" />
          Alertas deste contrato
        </h2>
      </div>

      {isLoading ? (
        <SkeletonText lines={2} />
      ) : configs.length === 0 ? (
        <p className="text-sm text-gray-500 mb-3">
          Nenhuma configuração específica — usando os padrões globais do sistema.
        </p>
      ) : (
        <ul className="divide-y divide-gray-100 text-sm mb-3">
          {configs.map((c) => (
            <li key={c.id} className="py-2 flex items-center justify-between">
              <span className="text-gray-700">
                {c.daysBefore} dias antes · {ALERT_TYPE_LABEL[c.alertType] ?? c.alertType}
              </span>
              {canEdit && (
                <button
                  onClick={() => desativar({ id: contractId, configId: c.id })}
                  className="text-gray-400 hover:text-red-600"
                  title="Remover"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {canEdit && (
        <div className="flex items-end gap-2">
          <div className="w-32">
            <TextInput label="Dias de antecedência" type="number" min={1} value={daysBefore} onChange={(e) => setDaysBefore(e.target.value)} />
          </div>
          <div className="w-40">
            <AutocompleteSelect label="Tipo" options={ALERT_TYPE_OPTIONS} value={alertType} onChange={setAlertType} />
          </div>
          <Button
            size="sm"
            disabled={!daysBefore || Number(daysBefore) < 1}
            loading={isCreating}
            icon={<Plus className="w-3.5 h-3.5" />}
            onClick={() => criar({ id: contractId, daysBefore: Number(daysBefore), alertType })}
          >
            Adicionar
          </Button>
        </div>
      )}
    </div>
  );
}
